import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { createPortal } from "react-dom";
import usePublishedLeaguesVisibility from "../../features/leagues/shared/hooks/usePublishedLeaguesVisibility";
import { getLeagueTexts } from "../../features/leagues/shared/leagueTexts";
import { useAuth } from "../../features/auth/shared/hooks/useAuth";
import brandLogo from "../../assets/images/tnp-logo-neon.png";
import {
  deleteAllNotifications,
  deleteNotification,
  listMyNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
} from "../../features/profile/shared/services/notificationsService";
import {
  cancelMyMatchParticipant,
  requestJoinMatch,
} from "../../features/matches/shared/services/matchesService";
import { siteTexts } from "../i18n/siteTexts";
import {
  hasSeenSiteLanguagePrompt,
  markSiteLanguagePromptSeen,
  SiteLanguage,
  useSiteLanguage,
} from "../i18n/language";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import Button from "./Button";
import { formatDateTimeLabel } from "../utils/dateTime";
import "./Header.css";

type HeaderNavKey =
  | "about"
  | "challenges"
  | "tournaments"
  | "ranking"
  | "athletes"
  | "matches"
  | "clubs"
  | "admin"
  | "profile"
  | "";

function resolveActiveNavKey(pageName: string | null): HeaderNavKey {
  if (!pageName) return "";
  if (pageName === "about") return "about";
  if (
    pageName === "challengesHub" ||
    pageName === "challengesOrganizersDirectory" ||
    pageName === "challengesOfficial" ||
    pageName === "challengesOrganizer" ||
    pageName === "challenges" ||
    pageName === "challengeDetail" ||
    pageName === "challengeSuperEight" ||
    pageName === "challengeSuperTwelve" ||
    pageName === "challengePoco"
  ) {
    return "challenges";
  }
  if (
    pageName === "tournaments" ||
    pageName === "tournamentsOfficial" ||
    pageName === "tournamentsOrganizers" ||
    pageName === "tournamentDetail"
  ) {
    return "tournaments";
  }
  if (
    pageName === "rankingHub" ||
    pageName === "rankingOrganizersDirectory" ||
    pageName === "rankingOfficial" ||
    pageName === "rankingOrganizer" ||
    pageName === "ranking"
  ) {
    return "ranking";
  }
  if (pageName === "athletes" || pageName === "publicPlayerProfile") {
    return "athletes";
  }
  if (pageName === "matches" || pageName === "matchDetail") {
    return "matches";
  }
  if (pageName === "clubs" || pageName === "clubDetail") {
    return "clubs";
  }
  if (
    pageName === "admin" ||
    pageName === "adminTournaments" ||
    pageName === "adminCreateChallenge" ||
    pageName === "adminCreateTournament" ||
    pageName === "adminChallengeEdit" ||
    pageName === "adminChallengeResults" ||
    pageName === "profileAdmin"
  ) {
    return "admin";
  }
  if (pageName === "profileUser") return "profile";
  return "";
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 4H4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h6" />
      <path d="M14 8l4 4-4 4" />
      <path d="M8 12h10" />
    </svg>
  );
}

function LanguageFlag({ language }: { language: SiteLanguage }) {
  return (
    <span
      className={`nav-language-flag nav-language-flag--${language}`}
      aria-hidden="true"
    >
      <span />
    </span>
  );
}

function LanguagePicker({
  className = "",
  language,
  supportedLanguages,
  onSelectLanguage,
}: {
  className?: string;
  language: SiteLanguage;
  supportedLanguages: SiteLanguage[];
  onSelectLanguage: (nextLanguage: SiteLanguage) => void;
}) {
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();
  const currentLanguageName = siteTexts.common.language.options[language];

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (pickerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div
      className={`nav-language-picker ${className}`.trim()}
      ref={pickerRef}
    >
      <button
        type="button"
        className="nav-language-current"
        aria-label={`${siteTexts.common.language.label}: ${currentLanguageName}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((current) => !current)}
      >
        <LanguageFlag language={language} />
      </button>
      {open && (
        <div
          id={menuId}
          className="nav-language-menu"
          role="listbox"
          aria-label={siteTexts.common.language.label}
        >
          {supportedLanguages.map((item) => (
            <button
              key={item}
              type="button"
              className={`nav-language-option${
                item === language ? " is-active" : ""
              }`}
              role="option"
              aria-selected={item === language}
              onClick={() => {
                setOpen(false);
                onSelectLanguage(item);
              }}
            >
              <LanguageFlag language={item} />
              <span>{siteTexts.common.language.options[item]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationCenter({
  token,
  onNavigate,
}: {
  token: string;
  onNavigate: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [runningInviteActionId, setRunningInviteActionId] = useState<number | null>(
    null,
  );
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();
  const texts = siteTexts.header.notifications;

  const applyResponse = (response: {
    unreadCount: number;
    notifications: NotificationItem[];
  }) => {
    setItems(response.notifications || []);
    setUnreadCount(Number(response.unreadCount || 0));
  };

  const loadNotifications = useCallback(
    (showLoading = false) => {
      if (showLoading) setLoading(true);
      setError("");
      listMyNotifications(token)
        .then(applyResponse)
        .catch((err) => {
          setError(err instanceof Error ? err.message : texts.error);
        })
        .finally(() => {
          if (showLoading) setLoading(false);
        });
    },
    [texts.error, token],
  );

  useEffect(() => {
    loadNotifications(false);
    const interval = window.setInterval(() => loadNotifications(false), 60000);
    return () => window.clearInterval(interval);
  }, [loadNotifications]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const onToggle = () => {
    setOpen((current) => {
      const next = !current;
      if (next) loadNotifications(true);
      return next;
    });
  };

  const onMarkRead = (id: number) => {
    markNotificationRead(token, id).then(applyResponse).catch(() => setError(texts.error));
  };

  const onMarkAllRead = () => {
    markAllNotificationsRead(token).then(applyResponse).catch(() => setError(texts.error));
  };

  const onRemove = (id: number) => {
    deleteNotification(token, id).then(applyResponse).catch(() => setError(texts.error));
  };

  const onRemoveAll = () => {
    deleteAllNotifications(token).then(applyResponse).catch(() => setError(texts.error));
  };

  const onOpenNotification = (item: NotificationItem) => {
    if (item.status === "unread") void markNotificationRead(token, item.id).then(applyResponse);
    if (item.actionUrl) {
      if (/^https?:\/\//i.test(item.actionUrl)) {
        window.location.assign(item.actionUrl);
      } else {
        const nextHash = item.actionUrl.replace(/^#/, "");
        const nextHashWithPrefix = `#${nextHash}`;
        if (window.location.hash === nextHashWithPrefix) {
          window.dispatchEvent(new HashChangeEvent("hashchange"));
        } else {
          window.location.hash = nextHash;
        }
      }
    }
    setOpen(false);
    onNavigate();
  };

  const matchInviteIdFor = (item: NotificationItem) => {
    if (item.type !== "open_match_invite") return 0;
    const matchId = Number(item.metadata?.matchId || item.metadata?.relatedEntityId || 0);
    return Number.isFinite(matchId) && matchId > 0 ? matchId : 0;
  };

  const onReviewInviteNotification = (
    event: ReactMouseEvent<HTMLButtonElement>,
    item: NotificationItem,
    action: "accept" | "decline",
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const matchId = matchInviteIdFor(item);
    if (!matchId || runningInviteActionId === item.id) return;
    setError("");
    setRunningInviteActionId(item.id);
    const request =
      action === "accept"
        ? requestJoinMatch(token, matchId)
        : cancelMyMatchParticipant(token, matchId);
    request
      .then(() => deleteNotification(token, item.id))
      .then(applyResponse)
      .catch((err) => {
        setError(err instanceof Error ? err.message : texts.error);
      })
      .finally(() => {
        setRunningInviteActionId(null);
      });
  };

  const actionLabelFor = (item: NotificationItem) => {
    const key = String(item.metadata?.actionLabelKey || "");
    if (key === "payNow") return texts.actions.payNow;
    if (key === "viewRegistration") return texts.actions.viewRegistration;
    if (key === "viewGames") return texts.actions.viewGames;
    return "";
  };

  return (
    <div className="nav-notifications" ref={panelRef}>
      <button
        type="button"
        className="nav-notifications-button"
        aria-label={texts.ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="material-symbols-outlined" aria-hidden="true">
          notifications
        </span>
        {unreadCount > 0 && (
          <span className="nav-notifications-count">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <section
          id={panelId}
          className="nav-notifications-panel"
          role="dialog"
          aria-label={texts.title}
        >
          <div className="nav-notifications-header">
            <h2>{texts.title}</h2>
            <div className="nav-notifications-header-actions">
              <button type="button" onClick={onMarkAllRead} disabled={!unreadCount}>
                {texts.markAllRead}
              </button>
              <button type="button" onClick={onRemoveAll} disabled={!items.length}>
                {texts.removeAll}
              </button>
            </div>
          </div>
          {loading ? (
            <p className="nav-notifications-state">{texts.loading}</p>
          ) : error ? (
            <p className="nav-notifications-state">{error || texts.error}</p>
          ) : items.length === 0 ? (
            <p className="nav-notifications-state">{texts.empty}</p>
          ) : (
            <div className="nav-notifications-list">
              {items.map((item) => (
                <article
                  key={item.id}
                  className={`nav-notification-item${
                    item.status === "unread" ? " is-unread" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="nav-notification-main"
                    onClick={() => onOpenNotification(item)}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      {item.icon || "notifications"}
                    </span>
                    <span>
                      <strong>{item.title}</strong>
                      <small>{item.description}</small>
                      <time>{formatDateTimeLabel(item.createdAt)}</time>
                      {actionLabelFor(item) && (
                        <b className="nav-notification-action-label">
                          {actionLabelFor(item)}
                        </b>
                      )}
                    </span>
                  </button>
                  <div className="nav-notification-actions">
                    {item.status === "unread" && (
                      <button type="button" onClick={() => onMarkRead(item.id)}>
                        {texts.markRead}
                      </button>
                    )}
                    {matchInviteIdFor(item) > 0 && (
                      <div className="nav-notification-invite-actions">
                        <button
                          type="button"
                          className="nav-notification-invite-action nav-notification-invite-action--accept"
                          aria-label={texts.actions.acceptInvite}
                          title={texts.actions.acceptInvite}
                          onClick={(event) =>
                            onReviewInviteNotification(event, item, "accept")
                          }
                          disabled={runningInviteActionId === item.id}
                        >
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            check
                          </span>
                        </button>
                        <button
                          type="button"
                          className="nav-notification-invite-action nav-notification-invite-action--decline"
                          aria-label={texts.actions.declineInvite}
                          title={texts.actions.declineInvite}
                          onClick={(event) =>
                            onReviewInviteNotification(event, item, "decline")
                          }
                          disabled={runningInviteActionId === item.id}
                        >
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            close
                          </span>
                        </button>
                      </div>
                    )}
                    <button type="button" onClick={() => onRemove(item.id)}>
                      {texts.remove}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default function Header() {
  const showLeagues = usePublishedLeaguesVisibility();
  const { user, token, isAdmin, isOrganizer, signOut } = useAuth();
  const { language, setLanguage, supportedLanguages } = useSiteLanguage();
  const [homeScrolled, setHomeScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeNavKey, setActiveNavKey] = useState<HeaderNavKey>("");
  const [showLanguagePrompt, setShowLanguagePrompt] = useState(
    () => !hasSeenSiteLanguagePrompt(),
  );

  useEffect(() => {
    const updateHeaderState = () => {
      const pageName = document.body.getAttribute("data-page");
      const isHome = pageName === "home";
      if (!isHome) {
        setHomeScrolled(false);
      } else {
        setHomeScrolled(window.scrollY > 60);
      }
      setActiveNavKey(resolveActiveNavKey(pageName));
    };

    const closeMenuOnNavigate = () => setMenuOpen(false);
    const closeMenuOnResize = () => {
      // Avoid leaving the mobile menu open when switching to desktop width.
      if (window.innerWidth > 768) setMenuOpen(false);
    };

    const observer = new MutationObserver(updateHeaderState);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-page"],
    });

    updateHeaderState();
    window.addEventListener("scroll", updateHeaderState, { passive: true });
    window.addEventListener("hashchange", updateHeaderState);
    window.addEventListener("hashchange", closeMenuOnNavigate);
    window.addEventListener("resize", closeMenuOnResize, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", updateHeaderState);
      window.removeEventListener("hashchange", updateHeaderState);
      window.removeEventListener("hashchange", closeMenuOnNavigate);
      window.removeEventListener("resize", closeMenuOnResize);
    };
  }, []);

  const closeMenu = () => setMenuOpen(false);
  const onSignOut = () => {
    signOut().finally(() => {
      closeMenu();
      window.location.hash = "#/login";
    });
  };
  const firstName =
    user?.name?.split(" ")[0] || siteTexts.header.fallbackPlayerName;
  const onSelectLanguage = (nextLanguage: SiteLanguage) => {
    if (nextLanguage === language) return;
    setLanguage(nextLanguage);
    window.location.reload();
  };
  const onSelectInitialLanguage = (nextLanguage: SiteLanguage) => {
    const changed = nextLanguage !== language;
    setLanguage(nextLanguage);
    markSiteLanguagePromptSeen();
    setShowLanguagePrompt(false);
    if (changed) window.location.reload();
  };

  useBodyScrollLock(showLanguagePrompt);

  return (
    <>
      {showLanguagePrompt &&
        createPortal(
          <div className="language-modal-backdrop" role="presentation">
            <section
              className="language-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="language-modal-title"
              aria-describedby="language-modal-description"
            >
              <img src={brandLogo} alt="TNP" className="language-modal-logo" />
              <h2 id="language-modal-title">
                {siteTexts.common.language.modalTitle}
              </h2>
              <p id="language-modal-description">
                {siteTexts.common.language.modalDescription}
              </p>
              <div className="language-modal-options">
                {supportedLanguages.map((item) => (
                  <button
                    key={`language-modal-${item}`}
                    type="button"
                    className={`language-modal-option${
                      item === language ? " is-active" : ""
                    }`}
                    onClick={() => onSelectInitialLanguage(item)}
                  >
                    <LanguageFlag language={item} />
                    <span>{siteTexts.common.language.options[item]}</span>
                  </button>
                ))}
              </div>
            </section>
          </div>,
          document.body,
        )}
      <header className={`site-header${homeScrolled ? " home-scrolled" : ""}`}>
        <div className="nav-inner">
        <a className="brand" href="#/">
          <img src={brandLogo} alt="TNP" className="brand-mark" />
        </a>
        {user && (
          <span className="mobile-user-greeting">
            <span className="nav-user-greeting-copy">
              <span className="nav-user-greeting-hello">
                {siteTexts.header.hello},
              </span>
              <strong>{firstName}</strong>
            </span>
          </span>
        )}
        {user && token && (
          <div className="mobile-notifications-slot">
            <NotificationCenter token={token} onNavigate={closeMenu} />
          </div>
        )}
        <button
          type="button"
          className="nav-toggle"
          aria-label={
            menuOpen ? siteTexts.header.closeMenu : siteTexts.header.openMenu
          }
          aria-expanded={menuOpen}
          aria-controls="site-nav-panel"
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          <span className="nav-toggle-icon" aria-hidden="true" />
        </button>

        <div
          id="site-nav-panel"
          className={`nav-panel${menuOpen ? " is-open" : ""}`}
        >
          <nav className="nav-links" aria-label="Primary navigation">
            <a
              href="#/sobre"
              onClick={closeMenu}
              className={activeNavKey === "about" ? "active" : undefined}
            >
              {siteTexts.header.nav.about}
            </a>
            <a
              href="#/desafios"
              onClick={closeMenu}
              className={activeNavKey === "challenges" ? "active" : undefined}
            >
              {siteTexts.header.nav.challenges}
            </a>
            <a
              href="#/torneios"
              onClick={closeMenu}
              className={activeNavKey === "tournaments" ? "active" : undefined}
            >
              {siteTexts.header.nav.tournaments}
            </a>
            <a
              href="#/ranking"
              onClick={closeMenu}
              className={activeNavKey === "ranking" ? "active" : undefined}
            >
              {siteTexts.header.nav.ranking}
            </a>
            <a
              href="#/atletas"
              onClick={closeMenu}
              className={activeNavKey === "athletes" ? "active" : undefined}
            >
              {siteTexts.header.nav.athletes}
            </a>
            <a
              href="#/partidas"
              onClick={closeMenu}
              className={activeNavKey === "matches" ? "active" : undefined}
            >
              {siteTexts.header.nav.matches}
            </a>
            <a
              href="#/clubes"
              onClick={closeMenu}
              className={activeNavKey === "clubs" ? "active" : undefined}
            >
              {siteTexts.header.nav.clubs}
            </a>
            {/* Temporariamente ocultos */}
            {showLeagues && <a href="#/ligas" onClick={closeMenu}>{getLeagueTexts().title}</a>}
            {/* <a href="#/loja" onClick={closeMenu}>{siteTexts.header.nav.shop}</a> */}
            {user && (
              <a
                className={`nav-link mobile-profile-link${
                  activeNavKey === "profile" ? " active" : ""
                }`}
                href="#/perfil"
                onClick={closeMenu}
              >
                {siteTexts.header.nav.profile}
              </a>
            )}
            {(isAdmin || isOrganizer) && (
              <a
                className={`nav-admin${activeNavKey === "admin" ? " active" : ""}`}
                href="#/admin"
                onClick={closeMenu}
              >
                {siteTexts.header.nav.admin}
              </a>
            )}
            {user && (
              <button
                type="button"
                className="nav-link mobile-logout-link"
                onClick={onSignOut}
              >
                <span className="nav-logout-icon">
                  <LogoutIcon />
                </span>
                <span>{siteTexts.header.nav.logout}</span>
              </button>
            )}
            {!user && (
              <a className="nav-link mobile-profile-link" href="#/cadastro" onClick={closeMenu}>
                {siteTexts.header.nav.createAccount}
              </a>
            )}
            {!user && (
              <a className="nav-link mobile-profile-link" href="#/login" onClick={closeMenu}>
                {siteTexts.header.nav.login}
              </a>
            )}
            <LanguagePicker
              className="nav-language-picker-mobile"
              language={language}
              supportedLanguages={supportedLanguages}
              onSelectLanguage={onSelectLanguage}
            />
          </nav>
          <div className="nav-actions desktop-nav-actions">
            {user ? (
              <>
                <span className="nav-user">
                  <span className="nav-user-greeting-copy">
                    <span className="nav-user-greeting-hello">
                      {siteTexts.header.hello},
                    </span>
                    <strong>{firstName}</strong>
                  </span>
                </span>
                {token && (
                  <NotificationCenter token={token} onNavigate={closeMenu} />
                )}

                <a
                  className={`nav-profile-button${
                    activeNavKey === "profile" ? " active" : ""
                  }`}
                  href="#/perfil"
                  aria-label={siteTexts.header.nav.profile}
                  title={siteTexts.header.nav.profile}
                  onClick={closeMenu}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    account_circle
                  </span>
                </a>
                <button
                  type="button"
                  className="nav-logout-button"
                  onClick={onSignOut}
                  aria-label={siteTexts.header.nav.logout}
                  title={siteTexts.header.nav.logout}
                >
                  <span className="nav-logout-icon" aria-hidden="true">
                    <LogoutIcon />
                  </span>
                </button>
                <LanguagePicker
                  language={language}
                  supportedLanguages={supportedLanguages}
                  onSelectLanguage={onSelectLanguage}
                />
              </>
            ) : (
              <>
                <a className="nav-link" href="#/cadastro" onClick={closeMenu}>
                  {siteTexts.header.nav.createAccount}
                </a>

                <Button variant="nav" href="#/login" onClick={closeMenu}>
                  {siteTexts.header.nav.login}
                </Button>
                <LanguagePicker
                  language={language}
                  supportedLanguages={supportedLanguages}
                  onSelectLanguage={onSelectLanguage}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </header>
    </>
  );
}
